import { FC } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'

import Dashboard from 'layouts/Dashboard'
import { WallList } from 'components/Wall'
import { useBoardsList } from 'services/boards/boards.hooks'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'

const ROOT_PATH = '/board'
// Routes rendered as a centered card on desktop instead of the master-detail columns.
const SINGLE_COLUMN_PATHS = ['/board/new-event']

// Layout for the Komunikaty section ("Panel Klienta 3.0" redesign).
// Desktop: full-width card grid on the root path, full-width detail views on subroutes.
// Mobile: list with view pills on the root path, full-screen subpages elsewhere.
const BoardLayout: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const { data: boardsList = [] } = useBoardsList()
  const { leaseContractsList } = useLeaseContractsList()

  const hasActiveLease = (leaseContractsList || []).some(({ leaseStatus }) => leaseStatus === 0)
  const hasNonFrozenBoard = boardsList.some((board) => !board.investment.operationsFrozen)
  const canAddPost = hasActiveLease && hasNonFrozenBoard

  const isSingleColumn = SINGLE_COLUMN_PATHS.some((pattern) => matchPath(pattern, pathname))

  const actionSlot = canAddPost ? (
    <Button
      onClick={() => navigate('/board/new-event')}
      fullWidth={!isDesktop}
      sx={{
        backgroundColor: theme.r4r.navy,
        color: '#fff',
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: '13px',
        borderRadius: 0,
        padding: '11px 18px',
        whiteSpace: 'nowrap',
        boxShadow: 'none',
        ':hover': {
          backgroundColor: theme.r4r.ink,
          boxShadow: 'none'
        }
      }}>
      {formatMessage({ id: 'wallList.addPost', defaultMessage: '+ Dodaj do tablicy' })}
    </Button>
  ) : null

  if (isDesktop) {
    return (
      <Dashboard noHeader disableGutters>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh' }}>
          <Box sx={{ maxWidth: '1200px', px: '36px', pt: '28px', pb: '64px' }}>
            {isSingleColumn ? (
              <Box
                sx={{
                  maxWidth: '760px',
                  mx: 'auto',
                  backgroundColor: '#fff',
                  boxShadow: theme.r4r.cardShadow,
                  p: '28px'
                }}>
                <Outlet />
              </Box>
            ) : pathname === ROOT_PATH ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '20px',
                    pb: '16px'
                  }}>
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 700,
                      lineHeight: '28.8px',
                      color: theme.r4r.navy
                    }}>
                    {formatMessage({ id: 'dashboard.announcements', defaultMessage: 'Komunikaty' })}
                  </Typography>
                  {actionSlot}
                </Box>
                <WallList />
              </>
            ) : (
              <Outlet />
            )}
          </Box>
        </Box>
      </Dashboard>
    )
  }

  if (pathname === ROOT_PATH) {
    return (
      <Dashboard noHeader>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh', pb: '24px' }}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: theme.r4r.navy,
              textAlign: 'center',
              pt: '18px',
              pb: '14px'
            }}>
            {formatMessage({ id: 'dashboard.announcements', defaultMessage: 'Komunikaty' })}
          </Typography>
          {actionSlot && <Box sx={{ px: '16px', pb: '16px' }}>{actionSlot}</Box>}
          <Box sx={{ px: '16px' }}>
            <Outlet />
          </Box>
        </Box>
      </Dashboard>
    )
  }

  return <Outlet />
}

export default BoardLayout
